import { ServiceStateAppStrict } from 'state/service/app';
import { TMCoreAction } from './types';
import {
  addAvailableTableTag,
  addBusinessGroupTag,
  addExtraTableToParty,
  addParty,
  addTable,
  addTextMessage,
  addTextMessageToParty,
  addWalkInParty,
  applyEntitySetFromDb,
  assignBusinessServerToTables,
  assignPartyToTable,
  closeAllParties,
  cutServer,
  markTextMessagesAsRead,
  moveTable,
  removeAllTablesFromParty,
  removeAvailableTableTag,
  removeParty,
  removePartyFromTable,
  removeServer,
  removeTable,
  removeTextMessageFromParty,
  removeWalkInParty,
  replaceParty,
  resetTablePositions,
  setFloorplan,
  setPartyWithIdToParty,
  setTextMessagesForParty,
  setTimesForPartyWithId,
  updateParty,
  updatePartyState,
  updateTable,
  updateTablePartyState,
  updateTableState,
} from 'state/service/appTs/tmCoreActions';
import { assertNever } from '@tock/shared/utils/typeUtils';

/***********
 * CORE UPDATE FUNCTIONS
 *
 * These are the main functions that update the core model.
 ***********/

/**
 * Updates the given model by taking the given action.
 *
 * @argument model the model to update
 * @argument action the action to take on the model
 * @returns the updated model
 * @throws ServiceAppError if the action is illegal on the current model
 */
export function update(model: ServiceStateAppStrict, action: TMCoreAction): ServiceStateAppStrict {
  switch (action.type) {
    case 'AddAvailableTableTag':
      return addAvailableTableTag(model, action.key, action.val);
    case 'AddBusinessGroupTag':
      return addBusinessGroupTag(model, action.businessTag);
    case 'AddExtraTableToParty':
      return addExtraTableToParty(model, action.partyAndTable);
    case 'AddParty':
      return addParty(model, action.party, action.tableIds);
    case 'AddTable':
      return addTable(model, action.table);
    case 'AddTextMessage':
      return addTextMessage(model, action.textMessage);
    case 'AddTextMessageToParty':
      return addTextMessageToParty(model, {
        partyId: action.partyId,
        textMessage: action.textMessage,
      });
    case 'AddWalkInParty':
      return addWalkInParty(model, action.partyDescription);
    case 'ApplyEntitySetFromDb':
      return applyEntitySetFromDb(model, action.entitySetFromDb);
    case 'AssignBusinessServerToTable':
      return assignBusinessServerToTables(model, action.businessServerAndTables);
    case 'AssignPartyToTable':
      return assignPartyToTable(model, action.partyAndTable);
    case 'CloseAllParties':
      return closeAllParties(model, action.timestampSecs);
    case 'CutServer':
      return cutServer(model, { id: action.businessServerId });
    case 'MarkTextMessagesAsRead':
      return markTextMessagesAsRead(model, { partyId: action.partyId });
    case 'MoveTable':
      return moveTable(model, action.tableAndPosition);
    case 'RemoveAllTablesFromParty':
      return removeAllTablesFromParty(model, action.partyId);
    case 'RemoveAvailableTableTag':
      return removeAvailableTableTag(model, action.key, action.val);
    case 'RemoveParty':
      return removeParty(model, action.partyId);
    case 'RemovePartyFromTable':
      return removePartyFromTable(model, action.partyAndTable);
    case 'RemoveServer':
      return removeServer(model, { tableIds: action.tableIds });
    case 'RemoveTable':
      return removeTable(model, action.tableId);
    case 'RemoveTextMessageFromParty':
      return removeTextMessageFromParty(model, action.partyId, action.textMessage);
    case 'RemoveWalkInParty':
      return removeWalkInParty(model, { partyId: action.partyId });
    case 'ReplaceParty':
      return replaceParty(model, action.party);
    case 'ResetAppState':
      return action.newAppState;
    case 'ResetTablePositions':
      return resetTablePositions(model);
    case 'SetFloorplan':
      return setFloorplan(model, action.floorplan, action.tableAssignments);
    case 'SetPartyWithIdToParty':
      return setPartyWithIdToParty(model, action.party);
    case 'SetTextMessagesForParty':
      return setTextMessagesForParty(model, action.partyId, action.textMessages);
    case 'SetTimesForPartyWithId':
      return setTimesForPartyWithId(model, action.partyId, action.leftTime, action.seatedTime);
    case 'UpdateParty':
      return updateParty(model, action.partyUpdate);
    case 'UpdatePartyState':
      return updatePartyState(model, action.partyAndState);
    case 'UpdateTable':
      return updateTable(model, action.table);
    case 'UpdateTablePartyState':
      return updateTablePartyState(model, action.tableId, action.stateUpdateWithoutParty);
    case 'UpdateTableState':
      return updateTableState(model, action.tableAndState);
    // istanbul ignore next
    default:
      return assertNever(action);
  }
}
